import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronLeft, ChevronRight, Clock, TrendingDown, Eye } from "lucide-react";
import ScooterCard, { ScooterListing } from "@/components/scooter/ScooterCard";
import { supabase } from "@/integrations/supabase/client";
import { useReveal } from "@/hooks/useReveal";

interface Section {
  title: string;
  subtitle: string;
  icon: React.ElementType;
  listings: ScooterListing[];
  link: string;
}

const mapListing = (l: any): ScooterListing => ({
  id: l.id,
  title: l.title,
  price: Number(l.price),
  condition: l.condition,
  brand: l.brand,
  model: l.model,
  city: l.city,
  image: l.images?.[0] || "/placeholder.svg",
  dgt_approved: l.dgt_approved,
  created_at: l.created_at,
});

const HorizontalScroll = ({ children }: { children: React.ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    if (!ref.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = ref.current;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    checkScroll();
    const el = ref.current;
    el?.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    return () => {
      el?.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, []);

  const scroll = (dir: number) => {
    ref.current?.scrollBy({ left: dir * 320, behavior: "smooth" });
  };

  return (
    <div className="relative group">
      {canScrollLeft && (
        <button
          type="button"
          onClick={() => scroll(-1)}
          aria-label="Anterior"
          className="absolute -left-3 top-1/2 z-10 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-background border border-border shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      )}
      {canScrollRight && (
        <button
          type="button"
          onClick={() => scroll(1)}
          aria-label="Siguiente"
          className="absolute -right-3 top-1/2 z-10 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-background border border-border shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      )}
      <div
        ref={ref}
        className="flex gap-5 overflow-x-auto scroll-smooth pb-2 scrollbar-none"
        style={{ scrollbarWidth: "none" }}
      >
        {children}
      </div>
    </div>
  );
};

const ThematicSections = () => {
  const [sections, setSections] = useState<Section[]>([]);

  useEffect(() => {
    const load = async () => {
      const [recentRes, cheapRes, viewedRes] = await Promise.all([
        supabase
          .from("listings")
          .select("*")
          .eq("status", "active")
          .order("created_at", { ascending: false })
          .limit(8),
        supabase
          .from("listings")
          .select("*")
          .eq("status", "active")
          .order("price", { ascending: true })
          .limit(8),
        supabase
          .from("listings")
          .select("*")
          .eq("status", "active")
          .order("views_count", { ascending: false })
          .limit(8),
      ]);

      setSections([
        {
          title: "Recién publicados",
          subtitle: "Los últimos patinetes que acaban de llegar",
          icon: Clock,
          listings: (recentRes.data || []).map(mapListing),
          link: "/buscar?sort=recent",
        },
        {
          title: "Mejores precios",
          subtitle: "Los patinetes más baratos disponibles ahora",
          icon: TrendingDown,
          listings: (cheapRes.data || []).map(mapListing),
          link: "/buscar?sort=price_asc",
        },
        {
          title: "Los más vistos",
          subtitle: "Lo que más interesa a la comunidad",
          icon: Eye,
          listings: (viewedRes.data || []).map(mapListing),
          link: "/buscar?sort=views",
        },
      ]);
    };
    load();
  }, []);

  if (sections.every((s) => s.listings.length === 0)) return null;

  return (
    <>
      {sections.map((section) => {
        if (section.listings.length === 0) return null;
        return <ThematicBlock key={section.title} section={section} />;
      })}
    </>
  );
};

const ThematicBlock = ({ section }: { section: Section }) => {
  const { ref, visible } = useReveal<HTMLDivElement>();
  const SectionIcon = section.icon;
  return (
    <section className="border-b border-border py-12">
      <div ref={ref} className="container">
        <div
          className={`flex items-end justify-between mb-6 transition-all duration-700 ease-out ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="relative inline-flex">
              <div
                className="absolute inset-0 -z-10 rounded-xl bg-gradient-to-br from-primary to-accent opacity-30 blur-md"
                aria-hidden
              />
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent text-primary-foreground shadow-md shadow-primary/20">
                <SectionIcon className="h-5 w-5" />
              </div>
            </div>
            <div>
              <h2 className="font-display text-xl font-bold md:text-2xl">{section.title}</h2>
              <p className="text-sm text-muted-foreground">{section.subtitle}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" asChild className="hidden sm:inline-flex">
            <Link to={section.link} className="group">
              Ver todos <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
        <HorizontalScroll>
          {section.listings.map((listing, idx) => (
            <div
              key={listing.id}
              style={{ transitionDelay: visible ? `${idx * 80}ms` : "0ms" }}
              className={`w-[280px] shrink-0 transition-all duration-700 ease-out ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
            >
              <ScooterCard listing={listing} />
            </div>
          ))}
        </HorizontalScroll>
        <div className="mt-4 text-center sm:hidden">
          <Button variant="outline" size="sm" asChild>
            <Link to={section.link}>Ver todos <ArrowRight className="ml-1 h-4 w-4" /></Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ThematicSections;
